import React, { useState, useEffect } from 'react';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../lib/utils';
import { CALENDAR_ICONS, getCalendarIcon } from '../lib/calendarIcons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Plus, 
  Trash2,
  Edit,
  Newspaper,
  Globe,
  Settings as SettingsIcon,
  Ruler,
  Save,
  Check,
  AlertTriangle,
  Database,
  FolderOpen,
  Upload,
  File as FileIcon,
  Image as ImageIcon,
  FileText as FileTextIcon,
  Users,
  Download,
  UploadCloud,
  ChevronDown,
  Package as PackageIcon,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { useFeatures } from '../lib/features';
import ProspectFields, { PROSPECT_DEFAULTS, prospectState, prospectPayload } from '../components/ProspectFields';

// Reusable collapsible section wrapper. Persists open/closed state per key in
// localStorage so a user's preferred layout survives reloads.
const CollapsibleSection = ({
  icon: Icon,
  title,
  description,
  testid,
  headerRight,
  defaultOpen = false,
  danger = false,
  storageKey,
  children,
}) => {
  // Sections that belong to a module this property has switched off are not shown.
  const { settingsSectionHidden } = useFeatures();
  const hidden = settingsSectionHidden(testid);
  const persistKey = storageKey || `settings-section-${testid || title}`;
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(persistKey);
      if (stored === 'open') return true;
      if (stored === 'closed') return false;
    } catch (_) { /* ignore */ }
    return defaultOpen;
  });

  const toggle = () => {
    setOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(persistKey, next ? 'open' : 'closed'); } catch (_) { /* ignore */ }
      return next;
    });
  };

  if (hidden) return null;

  const borderClass = danger ? 'border-red-900/50' : 'border-[#1E293B]';
  const iconColor   = danger ? 'text-red-400' : 'text-[#3B82F6]';
  const titleColor  = danger ? 'text-red-400' : 'text-[#F8FAFC]';

  return (
    <div className={`bg-[#121212] border ${borderClass} rounded-md`} data-testid={testid}>
      <div
        className={`p-4 flex items-center gap-3 cursor-pointer select-none ${open ? `border-b ${borderClass}` : ''}`}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        {Icon && <Icon className={`w-5 h-5 ${iconColor} shrink-0`} />}
        <div className="flex-1 min-w-0">
          <h2 className={`text-lg font-semibold ${titleColor}`}>{title}</h2>
          {description && <p className="text-sm text-[#94A3B8]">{description}</p>}
        </div>
        {headerRight && (
          <span
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            className="shrink-0"
          >
            {headerRight}
          </span>
        )}
        <ChevronDown
          className={`w-5 h-5 text-[#94A3B8] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </div>
      {open && <div>{children}</div>}
    </div>
  );
};

// Packages: preset bundles of items that can be dropped into an order.
const PackagesSection = () => {
  const [packages, setPackages] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pk, pt] = await Promise.all([api.get('/packages'), api.get('/product-types')]);
      setPackages(pk); setProductTypes(pt);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (pkg) => {
    if (!window.confirm(`Delete package "${pkg.name}"?`)) return;
    try { await api.delete(`/packages/${pkg.id}`); toast.success('Package deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const itemSummary = (pkg) => (pkg.items || []).map(i => `${i.quantity}× ${i.product_type}`).join(', ');

  return (
    <CollapsibleSection
      icon={PackageIcon}
      title="Packages"
      description="Preset bundles of items you can add to an order in one click"
      testid="packages-section"
      headerRight={
        <Button onClick={() => { setEditing(null); setShowModal(true); }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="add-package-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Package
        </Button>
      }
    >
      <div className="p-4">
        {loading ? (
          <p className="text-sm text-[#94A3B8]">Loading…</p>
        ) : packages.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">No packages yet. Create one (e.g. "Digital Presence Package") to speed up order entry.</p>
        ) : (
          <div className="space-y-2" data-testid="packages-list">
            {packages.map(pkg => (
              <div key={pkg.id} className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md" data-testid={`package-row-${pkg.id}`}>
                <div className="w-9 h-9 rounded-md bg-[#3B82F6]/10 flex items-center justify-center shrink-0"><PackageIcon className="w-4 h-4 text-[#3B82F6]" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#F8FAFC] font-medium truncate">
                    {pkg.name}
                    {pkg.show_to_prospects && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-[#3B82F6]/15 text-[#3B82F6]" data-testid={`package-prospects-chip-${pkg.id}`}>Prospects</span>}
                  </p>
                  <p className="text-xs text-[#94A3B8] truncate">{itemSummary(pkg)}</p>
                </div>
                <div className="text-right shrink-0">
                  {pkg.bundle_price != null
                    ? <span className="text-sm text-[#3B82F6] font-semibold">{formatCurrency(pkg.bundle_price)} <span className="text-[10px] text-[#475569]">bundle</span></span>
                    : <span className="text-xs text-[#475569]">sum of items</span>}
                </div>
                <button onClick={() => { setEditing(pkg); setShowModal(true); }} className="text-[#94A3B8] hover:text-[#F8FAFC] p-1.5" data-testid={`edit-package-${pkg.id}`}><Edit className="w-4 h-4" /></button>
                <button onClick={() => del(pkg)} className="text-red-400 hover:text-red-300 p-1.5" data-testid={`delete-package-${pkg.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <PackageModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        pkg={editing}
        productTypes={productTypes}
        onSave={() => { setShowModal(false); setEditing(null); load(); }}
      />
    </CollapsibleSection>
  );
};

const PackageModal = ({ isOpen, onClose, pkg, productTypes, onSave }) => {
  const blankRow = () => ({ product_type: '', quantity: 1, rate: '' });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [useBundle, setUseBundle] = useState(false);
  const [bundlePrice, setBundlePrice] = useState('');
  const [items, setItems] = useState([blankRow()]);
  const [prospect, setProspect] = useState(PROSPECT_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (pkg) {
        setName(pkg.name || '');
        setDescription(pkg.description || '');
        setUseBundle(pkg.bundle_price != null);
        setBundlePrice(pkg.bundle_price != null ? String(pkg.bundle_price) : '');
        setItems((pkg.items || []).map(i => ({ product_type: i.product_type, quantity: i.quantity || 1, rate: i.rate != null ? String(i.rate) : '' })));
        setProspect(prospectState(pkg));
      } else {
        setName(''); setDescription(''); setUseBundle(false); setBundlePrice(''); setItems([blankRow()]);
        setProspect(PROSPECT_DEFAULTS);
      }
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pkg]);

  const ptByName = (n) => (productTypes || []).find(p => p.name === n);
  const sumItems = items.reduce((s, it) => {
    const pt = ptByName(it.product_type);
    const rate = it.rate !== '' ? Number(it.rate) : (pt?.rate ?? 0);
    return s + rate * (Number(it.quantity) || 1);
  }, 0);

  const submit = async () => {
    setError('');
    if (!name.trim()) { setError('Package needs a name'); return; }
    const cleanItems = items.filter(it => it.product_type);
    if (!cleanItems.length) { setError('Add at least one item'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description || null,
        bundle_price: useBundle && bundlePrice !== '' ? Number(bundlePrice) : null,
        items: cleanItems.map(it => {
          const pt = ptByName(it.product_type);
          return {
            category: pt?.category || 'website',
            product_type: it.product_type,
            quantity: Math.max(1, Number(it.quantity) || 1),
            rate: it.rate !== '' ? Number(it.rate) : null,
          };
        }),
        ...prospectPayload(prospect),
      };
      if (pkg) await api.put(`/packages/${pkg.id}`, payload);
      else await api.post('/packages', payload);
      toast.success(pkg ? 'Package updated' : 'Package created');
      onSave();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="package-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PackageIcon className="w-5 h-5 text-[#3B82F6]" /> {pkg ? 'Edit Package' : 'New Package'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Package Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Digital Presence Package" className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]" data-testid="package-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]" data-testid="package-description-input" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[#F8FAFC]">Items</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => setItems([...items, blankRow()])} className="h-8 border-[#1E293B] bg-[#121212] text-[#F8FAFC]" data-testid="package-add-item-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Item</Button>
            </div>
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`package-item-${idx}`}>
                <Select value={it.product_type} onValueChange={(v) => setItems(items.map((x, i) => i === idx ? { ...x, product_type: v } : x))}>
                  <SelectTrigger className="h-9 bg-[#121212] border-[#1E293B] text-[#F8FAFC] flex-1" data-testid={`package-item-product-${idx}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]">
                    {(productTypes || []).map(p => <SelectItem key={p.id} value={p.name}>{p.name} — {formatCurrency(p.rate)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" min={1} value={it.quantity} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))} className="h-9 w-16 bg-[#121212] border-[#1E293B] text-[#F8FAFC]" title="Quantity" data-testid={`package-item-qty-${idx}`} />
                <Input type="number" value={it.rate} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, rate: e.target.value } : x))} placeholder="rate" className="h-9 w-24 bg-[#121212] border-[#1E293B] text-[#F8FAFC]" title="Rate override (blank = product default)" data-testid={`package-item-rate-${idx}`} />
                <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-red-400 p-1.5"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Checkbox checked={useBundle} onCheckedChange={setUseBundle} data-testid="package-use-bundle" />
            <Label className="text-[#94A3B8] text-sm">Fixed bundle price (overrides the sum of items)</Label>
          </div>
          {useBundle ? (
            <Input type="number" value={bundlePrice} onChange={(e) => setBundlePrice(e.target.value)} placeholder="e.g. 1000" className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]" data-testid="package-bundle-price" />
          ) : (
            <p className="text-sm text-[#94A3B8]">Items total: <span className="text-[#F8FAFC] font-medium">{formatCurrency(sumItems)}</span></p>
          )}

          <ProspectFields value={prospect} onChange={setProspect} testid="package-prospect" />

          {error && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="package-error">{error}</div>}

          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
            <Button type="button" onClick={submit} disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-package-btn">
              {saving ? 'Saving…' : (pkg ? 'Save Changes' : 'Create Package')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SettingsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isAccounting = user?.role === 'accounting';
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [activeCategory, setActiveCategory] = useState('digital_edition');
  const [pageDimensions, setPageDimensions] = useState({
    page_width: 11, page_height: 17, default_tax_rate: 16,
    margin_top: 0.5, margin_bottom: 0.5, margin_inside: 0.5, margin_outside: 0.5,
  });
  const [dimensionsSaving, setDimensionsSaving] = useState(false);
  const [dimensionsSaved, setDimensionsSaved] = useState(false);

  // Company branding + invoice email template
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyNameSaving, setCompanyNameSaving] = useState(false);
  const [companyNameSaved, setCompanyNameSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [invoiceEmailBody, setInvoiceEmailBody] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);

  // Default MXN/USD exchange rate
  const [defaultRate, setDefaultRate] = useState('');
  const [rateSaving, setRateSaving] = useState(false);
  const [rateSaved, setRateSaved] = useState(false);
  const [fetchingDof, setFetchingDof] = useState(false);

  const DEFAULT_INVOICE_EMAIL_BODY =
    'Hello {client_name},\n\n' +
    'Please find your invoice attached and included below for your convenience.\n\n' +
    'Thank you for your business. We appreciate the opportunity to work with you. ' +
    'If you have any questions regarding this invoice, please feel free to contact us.';

  useEffect(() => {
    loadProductTypes();
    loadSettings();
  }, []);

  const loadProductTypes = async () => {
    try {
      const data = await api.get('/product-types');
      setProductTypes(data);
    } catch (error) {
      console.error('Failed to load product types:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const data = await api.get('/settings');
      setPageDimensions({
        page_width: data.page_width || 11,
        page_height: data.page_height || 17,
        default_tax_rate: data.default_tax_rate ?? 16,
        margin_top: data.margin_top ?? 0.5,
        margin_bottom: data.margin_bottom ?? 0.5,
        margin_inside: data.margin_inside ?? 0.5,
        margin_outside: data.margin_outside ?? 0.5,
      });
      setCompanyLogoUrl(data.company_logo_url || '');
      setCompanyName(data.company_name || '');
      setInvoiceEmailBody(data.invoice_email_body || DEFAULT_INVOICE_EMAIL_BODY);
      setDefaultRate(data.default_mxn_usd_exchange_rate != null ? String(data.default_mxn_usd_exchange_rate) : '');
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCompanyLogoUrl(res.url);
      window.dispatchEvent(new CustomEvent('company-branding-changed', { detail: { url: res.url, base64: res.data_url } }));
    } catch (err) {
      alert('Logo upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove the current company logo?')) return;
    try {
      await api.put('/settings', { company_logo_url: '' });
      setCompanyLogoUrl('');
      window.dispatchEvent(new CustomEvent('company-branding-changed', { detail: { url: '' } }));
    } catch (err) {
      alert('Failed to remove logo: ' + (err.response?.data?.detail || err.message));
    }
  };

  const saveCompanyName = async () => {
    setCompanyNameSaving(true);
    try {
      await api.put('/settings', { company_name: companyName });
      window.dispatchEvent(new CustomEvent('company-branding-changed', { detail: { name: companyName } }));
      setCompanyNameSaved(true);
      setTimeout(() => setCompanyNameSaved(false), 2000);
    } catch (err) {
      alert('Failed to save company name: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCompanyNameSaving(false);
    }
  };

  const saveInvoiceEmailBody = async () => {
    setTemplateSaving(true);
    try {
      await api.put('/settings', { invoice_email_body: invoiceEmailBody });
      setTemplateSaved(true);
      setTimeout(() => setTemplateSaved(false), 2000);
    } catch (err) {
      alert('Failed to save template: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTemplateSaving(false);
    }
  };

  const saveDefaultRate = async () => {
    const v = parseFloat(defaultRate);
    if (!defaultRate || Number.isNaN(v) || v <= 0) {
      alert('Default MXN/USD exchange rate must be a number greater than 0.');
      return;
    }
    setRateSaving(true);
    try {
      await api.put('/settings', { default_mxn_usd_exchange_rate: v });
      setRateSaved(true);
      setTimeout(() => setRateSaved(false), 2000);
    } catch (err) {
      alert('Failed to save rate: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRateSaving(false);
    }
  };

  const fetchDefaultRateFromDof = async () => {
    setFetchingDof(true);
    try {
      // Same backend endpoint used by the Edition modal
      const today = new Date().toISOString().slice(0, 10);
      const res = await api.get(`/exchange-rate?date=${today}`);
      if (res?.rate) {
        setDefaultRate(String(res.rate));
      } else {
        alert('Could not fetch a rate from DOF for today.');
      }
    } catch (err) {
      alert('DOF fetch failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setFetchingDof(false);
    }
  };

  const savePageDimensions = async () => {
    setDimensionsSaving(true);
    try {
      await api.put('/settings', {
        page_width: parseFloat(pageDimensions.page_width) || 11,
        page_height: parseFloat(pageDimensions.page_height) || 17,
        default_tax_rate: parseFloat(pageDimensions.default_tax_rate) || 16,
        margin_top: parseFloat(pageDimensions.margin_top) || 0,
        margin_bottom: parseFloat(pageDimensions.margin_bottom) || 0,
        margin_inside: parseFloat(pageDimensions.margin_inside) || 0,
        margin_outside: parseFloat(pageDimensions.margin_outside) || 0,
      });
      setDimensionsSaved(true);
      setTimeout(() => setDimensionsSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setDimensionsSaving(false);
    }
  };

  const deleteProductType = async (id) => {
    if (!window.confirm('Delete this product type?')) return;
    try {
      await api.delete(`/product-types/${id}`);
      loadProductTypes();
    } catch (error) {
      console.error('Failed to delete product type:', error);
    }
  };

  const filteredProductTypes = productTypes.filter(pt => pt.category === activeCategory);

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center">
          <SettingsIcon className="w-5 h-5 text-[#3B82F6]" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">Settings</h1>
          <p className="text-[#94A3B8] mt-1">Manage product types and system configuration</p>
        </div>
      </div>

      {/* Print Edition Page Dimensions */}
      <CollapsibleSection
        icon={Ruler}
        title="Print Edition Page Dimensions"
        description="Set the physical page size for print editions. Used to calculate ad scaling in the Flatplan."
        testid="page-dimensions-section"
      >
        <div className="p-4">
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Width (inches)</Label>
              <Input
                data-testid="page-width-input"
                type="number"
                min="1"
                step="0.25"
                value={pageDimensions.page_width}
                onChange={(e) => setPageDimensions(prev => ({ ...prev, page_width: e.target.value }))}
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-32"
              />
            </div>
            <span className="text-[#94A3B8] pb-2 text-lg font-medium">&times;</span>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Height (inches)</Label>
              <Input
                data-testid="page-height-input"
                type="number"
                min="1"
                step="0.25"
                value={pageDimensions.page_height}
                onChange={(e) => setPageDimensions(prev => ({ ...prev, page_height: e.target.value }))}
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-32"
              />
            </div>
            <div className="border-l border-[#1E293B] pl-4 ml-2 space-y-2">
              <Label className="text-[#94A3B8]">Default Tax Rate (%)</Label>
              <Input
                data-testid="default-tax-rate-input"
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={pageDimensions.default_tax_rate}
                onChange={(e) => setPageDimensions(prev => ({ ...prev, default_tax_rate: e.target.value }))}
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-24"
              />
            </div>
            <Button
              onClick={savePageDimensions}
              disabled={dimensionsSaving}
              data-testid="save-dimensions-btn"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              {dimensionsSaved ? (
                <><Check className="w-4 h-4 mr-2" /> Saved</>
              ) : dimensionsSaving ? (
                'Saving...'
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save</>
              )}
            </Button>
          </div>
          <p className="text-xs text-[#475569] mt-3">
            Common sizes: Tabloid (11&Prime; &times; 17&Prime;), Broadsheet (15&Prime; &times; 22.75&Prime;), Berliner (12.4&Prime; &times; 18.5&Prime;)
          </p>

          {/* Page Margins */}
          <div className="mt-5 pt-5 border-t border-[#1E293B]">
            <p className="text-sm font-medium text-[#F8FAFC] mb-1">Page Margins (inches)</p>
            <p className="text-xs text-[#475569] mb-3">Shown as dashed guides on each Flatplan page. Inside = binding edge, Outside = trim edge (flips on left vs right pages).</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl">
              {[
                { key: 'margin_top',     label: 'Top' },
                { key: 'margin_bottom',  label: 'Bottom' },
                { key: 'margin_inside',  label: 'Inside' },
                { key: 'margin_outside', label: 'Outside' },
              ].map(({ key, label }) => (
                <div key={key} className="space-y-2">
                  <Label className="text-[#94A3B8]">{label}</Label>
                  <Input
                    data-testid={`${key.replace('_','-')}-input`}
                    type="number" min="0" step="0.125"
                    value={pageDimensions[key]}
                    onChange={(e) => setPageDimensions(prev => ({ ...prev, [key]: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-full"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Company Branding */}
      <CollapsibleSection
        icon={ImageIcon}
        title="Company Branding"
        description="Used in login, sidebar, and invoice PDFs. Company name is shown when no logo is set."
        testid="company-branding-section"
      >
        <div className="p-4 space-y-5">
          {/* Company Name */}
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-2 flex-1 min-w-[280px] max-w-md">
              <Label className="text-[#94A3B8]">Company Name</Label>
              <Input
                data-testid="company-name-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. Your Publication"
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              />
            </div>
            <Button
              onClick={saveCompanyName}
              disabled={companyNameSaving}
              data-testid="save-company-name-btn"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              {companyNameSaved ? (<><Check className="w-4 h-4 mr-2" /> Saved</>) : companyNameSaving ? 'Saving…' : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
            </Button>
          </div>

          {/* Logo */}
          <div className="flex items-center gap-6 flex-wrap pt-2 border-t border-[#1E293B]">
            <div className="w-32 h-32 bg-[#0A0A0A] border border-[#1E293B] rounded-md flex items-center justify-center overflow-hidden">
              {companyLogoUrl ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${companyLogoUrl}`} alt="Company logo" className="max-w-full max-h-full object-contain" data-testid="current-company-logo" />
              ) : (
                <span className="text-xs text-[#475569]">No logo</span>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-xs text-[#475569]">PNG / JPG / WEBP / SVG (max ~2 MB recommended).</p>
              <div className="flex gap-2 flex-wrap">
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="upload-logo-label">
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,.svg"
                    className="hidden"
                    onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                    data-testid="upload-logo-input"
                  />
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-md ${logoUploading ? 'bg-[#1E293B] text-[#94A3B8]' : 'bg-[#3B82F6] hover:bg-[#2563EB] text-white'}`}>
                    <Upload className="w-4 h-4" />
                    {logoUploading ? 'Uploading…' : (companyLogoUrl ? 'Replace Logo' : 'Upload Logo')}
                  </span>
                </label>
                {companyLogoUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={removeLogo}
                    className="border-[#1E293B] bg-[#121212] text-red-400 hover:bg-red-900/20"
                    data-testid="remove-logo-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Remove
                  </Button>
                )}
              </div>
              {companyLogoUrl && <p className="text-xs text-[#475569] break-all">{companyLogoUrl}</p>}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Invoice Email Template */}
      <CollapsibleSection
        icon={FileTextIcon}
        title="Invoice Email Body"
        description={<>Text shown at the top of invoice emails. Use <code className="text-[#3B82F6]">{'{client_name}'}</code>, <code className="text-[#3B82F6]">{'{invoice_number}'}</code>, <code className="text-[#3B82F6]">{'{total}'}</code>.</>}
        testid="invoice-email-template-section"
      >
        <div className="p-4 space-y-3">
          <Textarea
            value={invoiceEmailBody}
            onChange={(e) => setInvoiceEmailBody(e.target.value)}
            rows={7}
            className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] font-mono text-sm"
            placeholder="Hello {client_name},..."
            data-testid="invoice-email-body-textarea"
          />
          <div className="flex items-center gap-2">
            <Button
              onClick={saveInvoiceEmailBody}
              disabled={templateSaving}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              data-testid="save-email-body-btn"
            >
              {templateSaved ? (<><Check className="w-4 h-4 mr-2" /> Saved</>) : templateSaving ? 'Saving…' : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
            </Button>
            <Button
              variant="outline"
              onClick={() => setInvoiceEmailBody(DEFAULT_INVOICE_EMAIL_BODY)}
              className="border-[#1E293B] bg-[#121212] text-[#F8FAFC] hover:bg-[#1E293B]"
              data-testid="reset-email-body-btn"
            >
              Reset to Default
            </Button>
          </div>
        </div>
      </CollapsibleSection>

      {/* Default MXN/USD Exchange Rate */}
      <CollapsibleSection
        icon={Ruler}
        title="Default MXN/USD Exchange Rate"
        description="Used during invoice generation only when the current edition has no exchange rate set (after the warning prompt). Must be a positive number."
        testid="default-rate-section"
      >
        <div className="p-4 flex items-end gap-3 flex-wrap">
          <div className="space-y-1.5 max-w-xs flex-1 min-w-[200px]">
            <Label className="text-[#94A3B8] text-sm">Rate</Label>
            <Input
              type="number"
              step="0.0001"
              min="0"
              value={defaultRate}
              onChange={(e) => setDefaultRate(e.target.value)}
              placeholder="e.g. 17.4500"
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] font-mono"
              data-testid="default-rate-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={fetchDefaultRateFromDof}
            disabled={fetchingDof}
            className="border-[#1E293B] bg-[#121212] text-[#F8FAFC] hover:bg-[#1E293B]"
            data-testid="default-rate-dof-btn"
          >
            {fetchingDof ? 'Fetching…' : 'Get from DOF'}
          </Button>
          <Button
            onClick={saveDefaultRate}
            disabled={rateSaving}
            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            data-testid="default-rate-save-btn"
          >
            {rateSaved ? (<><Check className="w-4 h-4 mr-2" /> Saved</>) : rateSaving ? 'Saving…' : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
          </Button>
        </div>
      </CollapsibleSection>

      {/* Sections below this point are admin-only */}
      {!isAdmin && (
        <div className="bg-[#121212] border border-[#1E293B] rounded-md p-6 text-center">
          <p className="text-sm text-[#94A3B8]">
            {isAccounting
              ? 'You have access to Invoice Email Body and Default Exchange Rate only. Contact an admin for other settings.'
              : 'You do not have access to other settings.'}
          </p>
        </div>
      )}

      {isAdmin && (<>

      {/* Product Types Management */}
      <CollapsibleSection
        icon={Newspaper}
        title="Product Types"
        testid="product-types-section"
        headerRight={
          <Button
            onClick={() => {
              setSelectedProductType(null);
              setShowCreateModal(true);
            }}
            data-testid="add-product-type-btn"
            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product Type
          </Button>
        }
      >
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="p-4">
          <TabsList className="bg-[#0A0A0A] border border-[#1E293B]">
            <TabsTrigger 
              value="digital_edition" 
              className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white"
              data-testid="tab-digital-edition"
            >
              <Newspaper className="w-4 h-4 mr-2" />
              Print Edition
            </TabsTrigger>
            <TabsTrigger 
              value="website"
              className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white"
              data-testid="tab-website"
            >
              <Globe className="w-4 h-4 mr-2" />
              Website
            </TabsTrigger>
          </TabsList>

          <TabsContent value="digital_edition" className="mt-4">
            <p className="text-sm text-[#94A3B8] mb-4">
              Print Edition products can be placed on the Flatplan. Dimensions are in inches.
            </p>
            <ProductTypeList 
              productTypes={filteredProductTypes}
              onEdit={(pt) => {
                setSelectedProductType(pt);
                setShowCreateModal(true);
              }}
              onDelete={deleteProductType}
              loading={loading}
              dimensionUnit="in"
            />
          </TabsContent>

          <TabsContent value="website" className="mt-4">
            <p className="text-sm text-[#94A3B8] mb-4">
              Website products are for online advertising and cannot be placed on the Flatplan.
            </p>
            <ProductTypeList 
              productTypes={filteredProductTypes}
              onEdit={(pt) => {
                setSelectedProductType(pt);
                setShowCreateModal(true);
              }}
              onDelete={deleteProductType}
              loading={loading}
              dimensionUnit="px"
            />
          </TabsContent>
        </Tabs>
      </CollapsibleSection>

      {/* Create/Edit Product Type Modal */}
      <ProductTypeModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedProductType(null);
        }}
        productType={selectedProductType}
        defaultCategory={activeCategory}
        onSave={() => {
          loadProductTypes();
          setShowCreateModal(false);
          setSelectedProductType(null);
        }}
      />

      {/* User Management */}
      <UserManagementSection />

      {/* Packages */}
      <PackagesSection />

      {/* Sales Reps */}
      <SalesRepsSection />

      {/* Email Settings */}
      <EmailSettingsSection />

      {/* Industries Management */}
      <IndustriesSection />

      {/* File Manager */}
      <FileManagerSection />

      {/* Backup & Restore */}
      <BackupRestoreSection />

      {/* Danger Zone - Database Purge */}
      <DatabasePurgeSection />
      </>)}
    </div>
  );
};

// Product Type List Component
const ProductTypeList = ({ productTypes, onEdit, onDelete, loading, dimensionUnit }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
      </div>
    );
  }

  if (productTypes.length === 0) {
    return (
      <div className="text-center py-8 text-[#94A3B8]">
        <p>No product types in this category</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {productTypes.map((pt) => (
        <div 
          key={pt.id}
          className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1E293B] rounded-md hover:border-[#3B82F6]/50 transition-colors"
          data-testid={`product-type-${pt.id}`}
        >
          <div className="flex-1">
            <h3 className="font-medium text-[#F8FAFC]">
              {pt.name}
              {pt.show_to_prospects && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-[#3B82F6]/15 text-[#3B82F6]" data-testid={`product-prospects-chip-${pt.id}`}>Prospects</span>}
            </h3>
            <div className="flex items-center gap-4 mt-1 text-sm text-[#94A3B8]">
              {pt.width != null && pt.height != null && (
                <span>{pt.width}{dimensionUnit} &times; {pt.height}{dimensionUnit}</span>
              )}
              <span className="text-[#3B82F6] font-medium">{formatCurrency(pt.rate)}</span>
            </div>
            {pt.description && (
              <p className="text-xs text-[#94A3B8] mt-1">{pt.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(pt)}
              className="text-[#94A3B8] hover:text-[#F8FAFC]"
              data-testid={`edit-product-type-${pt.id}`}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(pt.id)}
              className="text-red-400 hover:text-red-300"
              data-testid={`delete-product-type-${pt.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Product Type Modal Component
const ProductTypeModal = ({ isOpen, onClose, productType, defaultCategory, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'digital_edition',
    width: '',
    height: '',
    rate: '',
    description: '',
    single_day_product: false,
    calendar_color: '',
    calendar_icon: '',
    ...PROSPECT_DEFAULTS,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (productType) {
      setFormData({
        name: productType.name || '',
        category: productType.category || 'digital_edition',
        width: productType.width || '',
        height: productType.height || '',
        rate: productType.rate || '',
        description: productType.description || '',
        single_day_product: !!productType.single_day_product,
        calendar_color: productType.calendar_color || '',
        calendar_icon: productType.calendar_icon || '',
        ...prospectState(productType),
      });
    } else {
      setFormData({
        name: '',
        category: defaultCategory || 'digital_edition',
        width: '',
        height: '',
        rate: '',
        description: '',
        single_day_product: false,
        calendar_color: '',
        calendar_icon: '',
        ...PROSPECT_DEFAULTS,
      });
    }
  }, [productType, defaultCategory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const isWebsite = formData.category === 'website';
      const payload = {
        name: formData.name,
        category: formData.category,
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        rate: parseFloat(formData.rate) || 0,
        description: formData.description || null,
        single_day_product: isWebsite ? !!formData.single_day_product : false,
        calendar_color: isWebsite ? (formData.calendar_color || null) : null,
        calendar_icon: isWebsite ? (formData.calendar_icon || null) : null,
        ...prospectPayload(formData),
      };

      if (productType) {
        await api.put(`/product-types/${productType.id}`, payload);
      } else {
        await api.post('/product-types', payload);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save product type:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product type');
    } finally {
      setSaving(false);
    }
  };

  const isDigitalEdition = formData.category === 'digital_edition';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{productType ? 'Edit Product Type' : 'New Product Type'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Category *</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]" data-testid="product-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="digital_edition" className="text-[#F8FAFC]">
                  <div className="flex items-center gap-2">
                    <Newspaper className="w-4 h-4" />
                    Print Edition
                  </div>
                </SelectItem>
                <SelectItem value="website" className="text-[#F8FAFC]">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Website
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Name *</Label>
            <Input
              data-testid="product-name-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Full Page, Banner Ad"
              required
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
            />
          </div>

          {isDigitalEdition && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Width (inches)</Label>
                <Input
                  data-testid="product-width-input"
                  type="number"
                  min="0"
                  step="0.25"
                  value={formData.width}
                  onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                  placeholder="e.g., 11"
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Height (inches)</Label>
                <Input
                  data-testid="product-height-input"
                  type="number"
                  min="0"
                  step="0.25"
                  value={formData.height}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  placeholder="e.g., 17"
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
            </div>
          )}

          {!isDigitalEdition && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Width (px)</Label>
                <Input
                  data-testid="product-width-input"
                  type="number"
                  min="0"
                  value={formData.width}
                  onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                  placeholder="e.g., 728"
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Height (px)</Label>
                <Input
                  data-testid="product-height-input"
                  type="number"
                  min="0"
                  value={formData.height}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  placeholder="e.g., 90"
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Rate ($) *</Label>
            <Input
              data-testid="product-rate-input"
              type="number"
              min="0"
              step="0.01"
              value={formData.rate}
              onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
              required
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Description</Label>
            <Input
              data-testid="product-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description"
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
            />
          </div>

          {!isDigitalEdition && (
            <div className="p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md space-y-3">
              <p className="text-[11px] uppercase tracking-wide text-[#94A3B8]">Digital scheduling</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.single_day_product}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, single_day_product: !!checked }))
                  }
                  data-testid="product-single-day-checkbox"
                />
                <span className="text-sm text-[#F8FAFC]">Single-day product</span>
              </label>
              <p className="text-[11px] text-[#475569] pl-6">
                Check for one-off products such as Email blasts, Social media posts, or Advertorials.
                Leave unchecked for date-range products such as Banner ads and Sandwich ads.
              </p>
              <div className="space-y-1 pt-2">
                <Label className="text-[#94A3B8] text-xs">Calendar Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.calendar_color || '#3B82F6'}
                    onChange={(e) =>
                      setFormData({ ...formData, calendar_color: e.target.value })
                    }
                    className="h-9 w-14 rounded bg-[#0A0A0A] border border-[#1E293B] cursor-pointer"
                    data-testid="product-calendar-color-input"
                  />
                  <Input
                    value={formData.calendar_color || ''}
                    onChange={(e) => setFormData({ ...formData, calendar_color: e.target.value })}
                    placeholder="#3B82F6"
                    className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 text-xs font-mono"
                  />
                  {formData.calendar_color && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, calendar_color: '' })}
                      className="text-[#94A3B8] h-9 px-2"
                    >
                      Reset
                    </Button>
                  )}
                </div>
                <p className="text-[11px] text-[#475569]">
                  Used on the Digital module calendar. Defaults to blue when empty.
                </p>
              </div>

              <div className="space-y-1 pt-3">
                <div className="flex items-center justify-between">
                  <Label className="text-[#94A3B8] text-xs">Calendar Icon</Label>
                  {formData.calendar_icon && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, calendar_icon: '' })}
                      className="text-[10px] text-[#94A3B8] hover:text-[#F8FAFC]"
                      data-testid="product-calendar-icon-reset"
                    >
                      Reset
                    </button>
                  )}
                </div>
                {/* Preview */}
                {(() => {
                  const PreviewIcon = getCalendarIcon(formData.calendar_icon);
                  const color = formData.calendar_color || '#3B82F6';
                  return (
                    <div className="flex items-center gap-2 p-2 bg-[#0A0A0A] border border-[#1E293B] rounded" data-testid="product-calendar-icon-preview">
                      <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: `${color}33`, borderLeft: `3px solid ${color}` }}>
                        <PreviewIcon className="w-4 h-4" style={{ color }} />
                      </div>
                      <span className="text-xs text-[#94A3B8]">
                        {formData.calendar_icon
                          ? (CALENDAR_ICONS.find(i => i.key === formData.calendar_icon)?.label || formData.calendar_icon)
                          : 'Default (Website)'}
                      </span>
                    </div>
                  );
                })()}
                {/* Grid picker */}
                <div
                  className="grid grid-cols-6 sm:grid-cols-8 gap-1 max-h-40 overflow-y-auto p-1.5 bg-[#0A0A0A] border border-[#1E293B] rounded"
                  data-testid="product-calendar-icon-grid"
                >
                  {CALENDAR_ICONS.map(({ key, label, Icon }) => {
                    const selected = formData.calendar_icon === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({ ...formData, calendar_icon: key })}
                        className={`aspect-square rounded flex items-center justify-center transition ${
                          selected
                            ? 'bg-[#3B82F6] text-white ring-2 ring-[#3B82F6]/60'
                            : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B]'
                        }`}
                        title={label}
                        data-testid={`product-calendar-icon-option-${key}`}
                      >
                        <Icon className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-[#475569]">
                  Shown next to bookings on the Digital calendar. Defaults to a globe when empty.
                </p>
              </div>
            </div>
          )}

          <ProspectFields
            value={formData}
            onChange={(v) => setFormData((prev) => ({ ...prev, ...v }))}
            testid="product-prospect"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              data-testid="save-product-type-btn"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              {saving ? 'Saving...' : (productType ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Email Settings Section
const EmailSettingsSection = () => {
  const [settings, setSettings] = useState({
    provider: 'resend', smtp_email: '', smtp_password: '',
    sender_email: '', cc_emails: '',
    bcc_emails: '', ms_connected: false, ms_user_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);

  React.useEffect(() => {
    const load = async () => {
      try {
        const data = await api.get('/email-settings');
        setSettings(data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
    // Check for OAuth callback result
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth_success')) {
      window.history.replaceState({}, '', '/settings');
      load();
    } else if (params.get('oauth_error')) {
      alert('Microsoft 365 connection failed: ' + params.get('oauth_error'));
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.put('/email-settings', {
        provider: settings.provider,
        smtp_email: settings.smtp_email,
        smtp_password: settings.smtp_password,
        sender_email: settings.sender_email,
        cc_emails: settings.cc_emails,
        bcc_emails: settings.bcc_emails
      });
      setSettings(updated);
      alert('Email settings saved!');
    } catch (err) { alert('Failed to save email settings.'); }
    finally { setSaving(false); }
  };

  const handleConnectMicrosoft = async () => {
    setConnecting(true);
    try {
      const data = await api.get('/oauth/microsoft/authorize');
      window.location.href = data.auth_url;
    } catch (err) {
      alert('Failed to start Microsoft 365 connection.');
      setConnecting(false);
    }
  };

  const handleDisconnectMicrosoft = async () => {
    if (!window.confirm('Disconnect Microsoft 365? Emails will switch back to Resend.')) return;
    try {
      await api.post('/oauth/microsoft/disconnect');
      setSettings(prev => ({ ...prev, provider: 'resend', ms_connected: false, ms_user_email: '' }));
    } catch (err) { alert('Failed to disconnect.'); }
  };

  if (loading) return null;

  return (
    <CollapsibleSection
      icon={Save}
      title="Email Settings"
      testid="email-settings-section"
    >
      <div className="p-4 space-y-4">
        {/* Provider selection */}
        <div className="space-y-2">
          <Label className="text-[#94A3B8] text-xs">Email Provider</Label>
          <Select value={settings.provider} onValueChange={(v) => setSettings(prev => ({ ...prev, provider: v }))}>
            <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] max-w-xs" data-testid="email-provider-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#1E293B]">
              <SelectItem value="resend" className="text-[#F8FAFC]">Resend (API-based)</SelectItem>
              <SelectItem value="gmail" className="text-[#F8FAFC]">Gmail (SMTP)</SelectItem>
              <SelectItem value="outlook_oauth" className="text-[#F8FAFC]">Microsoft 365 (OAuth)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Microsoft 365 OAuth */}
        {settings.provider === 'outlook_oauth' && (
          <div className="p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md space-y-3">
            {settings.ms_connected ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                  <span className="text-sm text-emerald-400 font-medium">Connected</span>
                  <span className="text-sm text-[#94A3B8]">as {settings.ms_user_email}</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleDisconnectMicrosoft} className="border-red-800 text-red-400 hover:bg-red-900/20" data-testid="disconnect-ms-btn">
                  Disconnect
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-[#94A3B8]">Connect your Microsoft 365 account to send invoices from your Outlook mailbox. Emails will appear in your Sent folder.</p>
                <Button onClick={handleConnectMicrosoft} disabled={connecting} className="bg-[#0078D4] hover:bg-[#106EBE] text-white" data-testid="connect-ms-btn">
                  {connecting ? 'Connecting...' : 'Connect Microsoft 365'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Gmail SMTP */}
        {settings.provider === 'gmail' && (
          <div className="grid grid-cols-2 gap-3 p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md">
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">Gmail Address</Label>
              <Input data-testid="smtp-email-input" type="email" value={settings.smtp_email} onChange={(e) => setSettings(prev => ({ ...prev, smtp_email: e.target.value }))} placeholder="you@gmail.com" className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">App Password</Label>
              <Input data-testid="smtp-password-input" type="password" value={settings.smtp_password} onChange={(e) => setSettings(prev => ({ ...prev, smtp_password: e.target.value }))} placeholder={settings.smtp_password === '***configured***' ? '***configured***' : 'App password'} className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9" />
            </div>
            <p className="col-span-2 text-[10px] text-[#475569]">Use a Gmail App Password (Google Account &gt; Security &gt; App Passwords). Emails appear in your Gmail Sent folder.</p>
          </div>
        )}

        {/* Sender email (for Resend) */}
        {settings.provider === 'resend' && (
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">Sender Email</Label>
            <Input data-testid="sender-email-input" type="email" value={settings.sender_email} onChange={(e) => setSettings(prev => ({ ...prev, sender_email: e.target.value }))} placeholder="billing@yourcompany.com" className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 max-w-xs" />
          </div>
        )}

        {/* CC / BCC */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">CC (comma-separated)</Label>
            <Input data-testid="email-cc-input" value={settings.cc_emails} onChange={(e) => setSettings(prev => ({ ...prev, cc_emails: e.target.value }))} placeholder="billing@example.com" className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">BCC (comma-separated)</Label>
            <Input data-testid="email-bcc-input" value={settings.bcc_emails} onChange={(e) => setSettings(prev => ({ ...prev, bcc_emails: e.target.value }))} placeholder="info@example.com" className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9" />
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-email-settings-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Email Settings'}
        </Button>
      </div>
    </CollapsibleSection>
  );
};

// Industries Management
const IndustriesSection = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const loadIndustries = async () => {
    try { setIndustries(await api.get('/industries')); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { loadIndustries(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await api.post('/industries', { name: newName.trim() });
      setNewName('');
      loadIndustries();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed to add industry.'); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this industry?')) return;
    try { await api.delete(`/industries/${id}`); loadIndustries(); }
    catch (err) { alert('Failed to delete.'); }
  };

  return (
    <CollapsibleSection
      icon={Globe}
      title="Industries"
      testid="industries-section"
    >
      <div className="p-4 space-y-3">
        <form onSubmit={handleAdd} className="flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New industry name..."
            className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] h-9 flex-1"
            data-testid="new-industry-input"
          />
          <Button type="submit" size="sm" disabled={adding || !newName.trim()} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-9" data-testid="add-industry-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </form>
        {loading ? (
          <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3B82F6] mx-auto"></div></div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {[...industries].sort((a, b) => a.name.localeCompare(b.name)).map(ind => (
              <div key={ind.id} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#1E293B] rounded-full" data-testid={`industry-${ind.id}`}>
                <span className="text-sm text-[#F8FAFC]">{ind.name}</span>
                <button onClick={() => handleDelete(ind.id)} className="text-[#475569] hover:text-red-400 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

// Backup & Restore Section
const BackupRestoreSection = () => {
  const [backing, setBacking] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const restoreInputRef = React.useRef(null);

  const handleBackup = async () => {
    setBacking(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Backup failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      a.download = match ? match[1] : `publishos_backup_${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Backup failed:', err);
      alert('Backup failed. Please try again.');
    } finally {
      setBacking(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      alert('Please select a .zip backup file.');
      return;
    }
    if (!window.confirm('This will REPLACE all current data with the backup, including CRM activity logs, advertiser sales-rep assignments, exchange rate settings, users, and roles. Are you sure?')) {
      if (restoreInputRef.current) restoreInputRef.current.value = '';
      return;
    }
    setRestoring(true);
    setRestoreResult(null);
    try {
      const result = await api.upload('/admin/restore', file);
      setRestoreResult({
        success: true,
        data: result.restored,
        skipped: result.skipped_collections || [],
        meta: result.backup_meta || {},
      });
    } catch (err) {
      console.error('Restore failed:', err);
      setRestoreResult({ success: false, error: 'Restore failed. The file may be corrupted or invalid.' });
    } finally {
      setRestoring(false);
      if (restoreInputRef.current) restoreInputRef.current.value = '';
    }
  };

  return (
    <CollapsibleSection
      icon={Database}
      title="Backup & Restore"
      description="Download a full backup or restore from a previous backup"
      testid="backup-restore-section"
    >
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Backup */}
          <div className="p-4 bg-[#0A0A0A] border border-[#1E293B] rounded-md space-y-3">
            <h3 className="text-sm font-semibold text-[#F8FAFC]">Create Backup</h3>
            <p className="text-xs text-[#94A3B8]">Downloads a complete ZIP of every PublishOS table + every uploaded file.</p>
            <ul
              className="text-[11px] text-[#94A3B8] space-y-0.5 list-disc list-inside pl-1"
              data-testid="backup-included-list"
            >
              <li>Users (incl. Admin / Accounting / User / Sales Rep roles &amp; passwords)</li>
              <li>Advertisers + Contacts + Sales-Rep Assignments</li>
              <li>Sales / CRM: Activities, Opportunities, Sales Rep records</li>
              <li>Bookings, Editions (with per-edition exchange rates), Flatplan placements</li>
              <li>Invoices, Invoice Audit Log, Factura counters</li>
              <li>Settings: Invoice Email Body, Default MXN/USD rate, Company Logo, Page Dimensions, Tax Rate</li>
              <li>Product Types, Rate Cards, Industries</li>
              <li>All uploaded creative files &amp; PDFs</li>
            </ul>
            <Button
              onClick={handleBackup}
              disabled={backing}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white w-full"
              data-testid="create-backup-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {backing ? 'Creating backup...' : 'Download Full Backup'}
            </Button>
          </div>

          {/* Restore */}
          <div className="p-4 bg-[#0A0A0A] border border-[#1E293B] rounded-md space-y-3">
            <h3 className="text-sm font-semibold text-[#F8FAFC]">Restore from Backup</h3>
            <p className="text-xs text-[#94A3B8]">
              Upload a backup ZIP to restore <strong>all</strong> tables. This will REPLACE current advertisers, bookings,
              invoices, sales activity, opportunities, advertiser assignments, exchange-rate settings, users and roles
              with what&apos;s in the file. Older backups (without CRM/Accounting data) still restore safely — missing
              tables are left untouched.
            </p>
            <input
              ref={restoreInputRef}
              type="file"
              accept=".zip"
              onChange={handleRestore}
              className="hidden"
              data-testid="restore-file-input"
            />
            <Button
              variant="outline"
              onClick={() => restoreInputRef.current?.click()}
              disabled={restoring}
              className="border-[#1E293B] text-[#94A3B8] w-full"
              data-testid="restore-backup-btn"
            >
              <UploadCloud className="w-4 h-4 mr-2" />
              {restoring ? 'Restoring...' : 'Upload & Restore Backup'}
            </Button>
          </div>
        </div>

        {restoreResult && (
          <div className={`p-3 rounded-md text-sm ${restoreResult.success ? 'bg-emerald-900/20 border border-emerald-800 text-emerald-400' : 'bg-red-900/20 border border-red-800 text-red-400'}`} data-testid="restore-result">
            {restoreResult.success ? (
              <>
                <p className="font-medium">Backup restored successfully:</p>
                {restoreResult.meta && (restoreResult.meta.backup_version || restoreResult.meta.created_at) && (
                  <p className="text-[11px] text-emerald-300/80 mt-0.5" data-testid="restore-meta">
                    Source: v{restoreResult.meta.backup_version || restoreResult.meta.version || 'legacy'}
                    {restoreResult.meta.created_at ? ` · ${new Date(restoreResult.meta.created_at).toLocaleString()}` : ''}
                    {restoreResult.meta.created_by ? ` · by ${restoreResult.meta.created_by}` : ''}
                  </p>
                )}
                <ul className="mt-1 list-disc list-inside text-xs">
                  {Object.entries(restoreResult.data).map(([key, val]) => (
                    <li key={key}>{key}: {val} records</li>
                  ))}
                </ul>
                {restoreResult.skipped && restoreResult.skipped.length > 0 && (
                  <p
                    className="text-[11px] text-amber-300 mt-2"
                    data-testid="restore-skipped-info"
                  >
                    Skipped (not present in this backup — current data preserved):{' '}
                    {restoreResult.skipped.join(', ')}
                  </p>
                )}
              </>
            ) : (
              <p>{restoreResult.error}</p>
            )}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

// Database Purge Section
const DatabasePurgeSection = () => {
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [purging, setPurging] = useState(false);
  const [result, setResult] = useState(null);

  const collections = [
    { id: 'advertisers', label: 'Advertisers database', description: 'All advertisers and their contacts' },
    { id: 'bookings', label: 'Bookings database', description: 'All ad bookings and placements' },
    { id: 'invoices', label: 'Accounting (Invoices) database', description: 'All invoices and payment records' },
    { id: 'editions', label: 'Editions database', description: 'All editions and section layouts' },
    { id: 'sales_reps', label: 'Sales Reps database', description: 'All sales representatives' },
    { id: 'uploaded_files', label: 'All uploaded files', description: 'All uploaded PDFs, images, and overlay files' },
  ];

  const toggleCollection = (id) => {
    setSelectedCollections(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
    setResult(null);
  };

  const canPurge = selectedCollections.length > 0 && confirmText === 'CONFIRM';

  const handlePurge = async () => {
    if (!canPurge) return;
    setPurging(true);
    setResult(null);
    try {
      const res = await api.post('/admin/purge', {
        collections: selectedCollections,
        confirmation: confirmText
      });
      setResult({ success: true, data: res.deleted });
      setSelectedCollections([]);
      setConfirmText('');
    } catch (err) {
      console.error('Purge failed:', err);
      setResult({ success: false, error: 'Failed to purge databases.' });
    } finally {
      setPurging(false);
    }
  };

  return (
    <CollapsibleSection
      icon={AlertTriangle}
      title="Danger Zone"
      description="Permanently delete database records. This action cannot be undone."
      testid="danger-zone-section"
      danger
    >
      <div className="p-4 space-y-4">
        <div className="space-y-3">
          {collections.map(coll => (
            <label
              key={coll.id}
              className="flex items-start gap-3 p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md cursor-pointer hover:border-red-900/50 transition-colors"
              data-testid={`purge-checkbox-${coll.id}`}
            >
              <Checkbox
                checked={selectedCollections.includes(coll.id)}
                onCheckedChange={() => toggleCollection(coll.id)}
                className="mt-0.5"
              />
              <div>
                <p className="text-sm font-medium text-[#F8FAFC]">{coll.label}</p>
                <p className="text-xs text-[#94A3B8]">{coll.description}</p>
              </div>
            </label>
          ))}
        </div>

        {selectedCollections.length > 0 && (
          <div className="space-y-3 p-4 bg-red-900/10 border border-red-900/30 rounded-md">
            <p className="text-sm text-red-400">
              You are about to permanently delete: <strong>{selectedCollections.join(', ')}</strong>
            </p>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Type <strong className="text-red-400">CONFIRM</strong> to proceed</Label>
              <Input
                data-testid="purge-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type CONFIRM here"
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] max-w-xs"
              />
            </div>
            <Button
              onClick={handlePurge}
              disabled={!canPurge || purging}
              data-testid="purge-submit-btn"
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            >
              <Database className="w-4 h-4 mr-2" />
              {purging ? 'Deleting...' : `Delete Selected Databases (${selectedCollections.length})`}
            </Button>
          </div>
        )}

        {result && (
          <div className={`p-3 rounded-md text-sm ${result.success ? 'bg-emerald-900/20 border border-emerald-800 text-emerald-400' : 'bg-red-900/20 border border-red-800 text-red-400'}`} data-testid="purge-result">
            {result.success ? (
              <>
                <p className="font-medium">Databases purged successfully:</p>
                <ul className="mt-1 list-disc list-inside text-xs">
                  {Object.entries(result.data).map(([key, val]) => (
                    <li key={key}>{key}: {val} records deleted</li>
                  ))}
                </ul>
              </>
            ) : (
              <p>{result.error}</p>
            )}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

// User Management Section
const UserManagementSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'user' });

  const loadUsers = async () => {
    try { setUsers(await api.get('/users')); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { loadUsers(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: formData.name, email: formData.email, role: formData.role };
      if (formData.password) payload.password = formData.password;
      if (editingUser) {
        if (!formData.password) delete payload.password;
        await api.put(`/users/${editingUser.id}`, payload);
      } else {
        if (!formData.password) { alert('Password required for new users'); return; }
        payload.password = formData.password;
        await api.post('/users', payload);
      }
      loadUsers();
      setShowForm(false);
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: 'user' });
    } catch (err) { alert(err?.response?.data?.detail || 'Failed to save user.'); }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({ name: u.name, email: u.email, password: '', role: u.role });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this user?')) return;
    try { await api.delete(`/users/${id}`); loadUsers(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed to delete.'); }
  };

  const roleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-900/30 text-purple-400 border-purple-800',
      accounting: 'bg-orange-900/30 text-orange-400 border-orange-800',
      sales_admin: 'bg-teal-900/30 text-teal-400 border-teal-800',
      user: 'bg-blue-900/30 text-blue-400 border-blue-800',
      sales_rep: 'bg-emerald-900/30 text-emerald-400 border-emerald-800',
    };
    return colors[role] || colors.user;
  };

  const roleLabel = (role) => {
    const labels = {
      admin: 'Admin',
      accounting: 'Accounting',
      sales_admin: 'Sales Admin',
      user: 'User',
      sales_rep: 'Sales Rep',
    };
    return labels[role] || role;
  };

  return (
    <CollapsibleSection
      icon={Users}
      title="Users"
      testid="user-management-section"
      headerRight={
        <Button variant="outline" size="sm" onClick={() => { setEditingUser(null); setFormData({ name: '', email: '', password: '', role: 'user' }); setShowForm(true); }} className="border-[#1E293B] text-[#94A3B8]" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      }
    >
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3B82F6] mx-auto"></div></div>
        ) : users.length > 0 ? (
          <div className="space-y-2">
            {users.map(u => (
              <div key={u.id || u._id} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md" data-testid={`user-row-${u.id || u._id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1E293B] flex items-center justify-center text-[#F8FAFC] text-sm font-medium">
                    {u.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#F8FAFC]">{u.name}</p>
                    <p className="text-xs text-[#94A3B8]">{u.email}</p>
                    {u.role === 'sales_rep' && (
                      <p
                        className="text-[10px] mt-0.5"
                        data-testid={`user-matched-rep-${u.id || u._id}`}
                      >
                        {u.matched_sales_rep ? (
                          <span className="text-emerald-400">
                            ✓ Sales Rep Profile: {u.matched_sales_rep.name}
                          </span>
                        ) : (
                          <span className="text-amber-400">
                            ⚠ No matching sales rep profile (has no clients of their own)
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  <span className={`ml-2 px-2 py-0.5 text-[10px] rounded-full border ${roleBadge(u.role)}`}>{roleLabel(u.role)}</span>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(u)} className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 w-8 p-0"><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id || u._id)} className="text-[#94A3B8] hover:text-red-400 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-4 text-[#94A3B8] text-sm">No users found</p>
        )}

        {showForm && (
          <form onSubmit={handleSave} className="p-3 bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-md space-y-3">
            <p className="text-sm font-medium text-[#F8FAFC]">{editingUser ? 'Edit' : 'Add'} User</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Name *</Label>
                <Input data-testid="user-name-input" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Email *</Label>
                <Input data-testid="user-email-input" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">{editingUser ? 'New Password (leave blank to keep)' : 'Password *'}</Label>
                <Input data-testid="user-password-input" type="password" value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} required={!editingUser} className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Role *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}>
                  <SelectTrigger className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#1E293B]">
                    <SelectItem value="admin" className="text-[#F8FAFC]" data-testid="user-role-option-admin">Admin</SelectItem>
                    <SelectItem value="accounting" className="text-[#F8FAFC]" data-testid="user-role-option-accounting">Accounting</SelectItem>
                    <SelectItem value="sales_admin" className="text-[#F8FAFC]" data-testid="user-role-option-sales-admin">Sales Admin</SelectItem>
                    <SelectItem value="user" className="text-[#F8FAFC]" data-testid="user-role-option-user">User</SelectItem>
                    <SelectItem value="sales_rep" className="text-[#F8FAFC]" data-testid="user-role-option-sales-rep">Sales Rep</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {editingUser && editingUser.role === 'sales_rep' && (
              <div
                className={`p-2 rounded text-xs ${editingUser.matched_sales_rep ? 'bg-emerald-900/20 border border-emerald-800 text-emerald-300' : 'bg-amber-900/20 border border-amber-800 text-amber-300'}`}
                data-testid="edit-user-matched-rep-info"
              >
                {editingUser.matched_sales_rep ? (
                  <>Sales Rep Profile Match: <strong>{editingUser.matched_sales_rep.name}</strong> — {editingUser.matched_sales_rep.email}</>
                ) : (
                  <>No matching sales rep profile found (email-based match). Create a Sales Rep record with the same email to assign advertisers to this user.</>
                )}
              </div>
            )}
            <p className="text-[10px] text-[#475569]">
              Admin: Full access. Accounting: User permissions + Invoice Email Body + Default MXN/USD exchange rate + assigning clients to sales reps. User: Everything except Settings; sees every client and its rep but cannot assign. Sales Rep: sees every client and its rep; edits, logs and claims only their own and unassigned clients, matched by the email on their Sales Rep record.
            </p>
            <div className="flex gap-2">
              <Button type="submit" size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-user-btn">
                <Save className="w-4 h-4 mr-1" /> {editingUser ? 'Update' : 'Create'}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowForm(false); setEditingUser(null); }} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
            </div>
          </form>
        )}
      </div>
    </CollapsibleSection>
  );
};

// Sales Reps Management
const SalesRepsSection = () => {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', weekly_meeting_target: 8 });

  const loadReps = async () => {
    try { setReps(await api.get('/sales-reps')); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Rule: an assigned client with no logged activity for N days goes back
  // to the unassigned pool. 0 = never.
  const [rules, setRules] = useState(null);
  const [daysInput, setDaysInput] = useState('');
  const [rulesMsg, setRulesMsg] = useState('');
  const [rulesBusy, setRulesBusy] = useState(false);

  const loadRules = async () => {
    try {
      const r = await api.get('/sales/settings');
      setRules(r);
      setDaysInput(String(r.rep_inactivity_days));
    } catch (err) { console.error(err); }
  };

  const saveRules = async () => {
    const days = parseInt(daysInput, 10);
    if (Number.isNaN(days) || days < 0) { setRulesMsg('Enter a number of days (0 turns the rule off).'); return; }
    setRulesBusy(true);
    setRulesMsg('');
    try {
      const r = await api.put('/sales/settings', { rep_inactivity_days: days });
      setRules(r);
      setDaysInput(String(r.rep_inactivity_days));
      setRulesMsg(r.rep_inactivity_days ? `Saved. Clients are released after ${r.rep_inactivity_days} days without activity.` : 'Saved. Clients are never released automatically.');
    } catch (err) {
      setRulesMsg(err?.response?.data?.detail || 'Could not save.');
    } finally { setRulesBusy(false); }
  };

  const releaseNow = async () => {
    if (!window.confirm(`Unassign ${rules.would_release_now} client(s) with no activity in ${rules.rep_inactivity_days} days now?`)) return;
    setRulesBusy(true);
    setRulesMsg('');
    try {
      const r = await api.post('/sales/release-inactive', {});
      setRulesMsg(`${r.released} client(s) went back to the unassigned pool.`);
      await loadRules();
    } catch (err) {
      setRulesMsg(err?.response?.data?.detail || 'Could not run the check.');
    } finally { setRulesBusy(false); }
  };

  React.useEffect(() => { loadReps(); loadRules(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const target = parseInt(formData.weekly_meeting_target, 10);
    if (Number.isNaN(target) || target < 0 || target > 50) { alert('Weekly meeting target must be a number from 0 to 50.'); return; }
    const body = { ...formData, weekly_meeting_target: target };
    try {
      if (editingRep) {
        await api.put(`/sales-reps/${editingRep.id}`, body);
      } else {
        await api.post('/sales-reps', body);
      }
      loadReps();
      setShowForm(false);
      setEditingRep(null);
      setFormData({ name: '', email: '', weekly_meeting_target: 8 });
    } catch (err) { alert('Failed to save sales rep.'); }
  };

  const handleEdit = (rep) => {
    setEditingRep(rep);
    setFormData({ name: rep.name, email: rep.email, weekly_meeting_target: Number.isInteger(rep.weekly_meeting_target) ? rep.weekly_meeting_target : 8 });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this sales rep?')) return;
    try { await api.delete(`/sales-reps/${id}`); loadReps(); }
    catch (err) { alert('Failed to delete.'); }
  };

  return (
    <CollapsibleSection
      icon={Users}
      title="Sales Reps"
      testid="sales-reps-section"
      headerRight={
        <Button variant="outline" size="sm" onClick={() => { setEditingRep(null); setFormData({ name: '', email: '', weekly_meeting_target: 8 }); setShowForm(true); }} className="border-[#1E293B] text-[#94A3B8]" data-testid="add-sales-rep-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Rep
        </Button>
      }
    >
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3B82F6] mx-auto"></div></div>
        ) : reps.length > 0 ? (
          <div className="space-y-2">
            {reps.map(rep => (
              <div key={rep.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md" data-testid={`sales-rep-${rep.id}`}>
                <div>
                  <p className="text-sm font-medium text-[#F8FAFC]">{rep.name}</p>
                  <p className="text-xs text-[#94A3B8]">{rep.email}</p>
                  <p className="text-xs text-[#94A3B8]" data-testid={`sales-rep-target-${rep.id}`}>
                    {(() => {
                      const t = Number.isInteger(rep.weekly_meeting_target) ? rep.weekly_meeting_target : 8;
                      return t ? `Sales Planner target: ${t} in-person meeting${t === 1 ? '' : 's'} a week` : 'Sales Planner target: none';
                    })()}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(rep)} className="text-[#94A3B8] hover:text-[#F8FAFC] h-8 w-8 p-0"><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(rep.id)} className="text-[#94A3B8] hover:text-red-400 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-4 text-[#94A3B8] text-sm">No sales reps added yet</p>
        )}

        {rules && (
          <div className="p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md space-y-2" data-testid="sales-rules">
            <p className="text-sm font-medium text-[#F8FAFC]">Release inactive clients</p>
            <p className="text-xs text-[#94A3B8]">
              A client assigned to a sales rep goes back to the unassigned pool when no activity has been logged for it in this many days.
              Clients with a follow-up booked for today or later are kept. Reps are warned {rules.warn_days} days ahead on their My day screen. Enter 0 to turn this off.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Days without activity</Label>
                <Input data-testid="rep-inactivity-days-input" type="number" min="0" max="3650" value={daysInput} onChange={(e) => setDaysInput(e.target.value)} className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9 w-32" />
              </div>
              <Button type="button" size="sm" disabled={rulesBusy} onClick={saveRules} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-sales-rules-btn">
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              {rules.rep_inactivity_days > 0 && rules.would_release_now > 0 && (
                <Button type="button" variant="outline" size="sm" disabled={rulesBusy} onClick={releaseNow} className="border-[#1E293B] text-[#94A3B8]" data-testid="release-inactive-now-btn">
                  Release {rules.would_release_now} now
                </Button>
              )}
            </div>
            <p className="text-xs text-[#475569]" data-testid="sales-rules-status">
              {rulesMsg || (rules.rep_inactivity_days
                ? `On: ${rules.rep_inactivity_days} days. Checked once a day. ${rules.would_release_now} client(s) would be released today.`
                : 'Off: clients stay with their rep until an admin reassigns them.')}
            </p>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSave} className="p-3 bg-[#0A0A0A] border border-[#3B82F6]/30 rounded-md space-y-3">
            <p className="text-sm font-medium text-[#F8FAFC]">{editingRep ? 'Edit' : 'Add'} Sales Rep</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Name *</Label>
                <Input data-testid="sales-rep-name-input" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#94A3B8] text-xs">Email *</Label>
                <Input data-testid="sales-rep-email-input" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-[#94A3B8] text-xs">In-person meetings a week (Sales Planner target, 0 = none)</Label>
                <Input data-testid="sales-rep-target-input" type="number" min="0" max="50" value={formData.weekly_meeting_target} onChange={(e) => setFormData(prev => ({ ...prev, weekly_meeting_target: e.target.value }))} className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9 w-32" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-sales-rep-btn">
                <Save className="w-4 h-4 mr-1" /> {editingRep ? 'Update' : 'Add'}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { setShowForm(false); setEditingRep(null); }} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
            </div>
          </form>
        )}
      </div>
    </CollapsibleSection>
  );
};

// File Manager Section
const FileManagerSection = () => {
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);

  const loadFiles = async () => {
    try {
      const data = await api.get('/files/list');
      setFiles(data);
    } catch (err) { console.error('Failed to load files:', err); }
    finally { setLoadingFiles(false); }
  };

  React.useEffect(() => { loadFiles(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await api.upload('/upload', file);
      await loadFiles();
    } catch (err) { alert('Upload failed.'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    setDeleting(filename);
    try {
      await api.delete(`/files/${filename}`);
      setFiles(prev => prev.filter(f => f.name !== filename));
    } catch (err) { alert('Delete failed.'); }
    finally { setDeleting(null); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type) => {
    if (['jpg', 'jpeg', 'png', 'webp'].includes(type)) return <ImageIcon className="w-4 h-4 text-emerald-400" />;
    if (type === 'pdf') return <FileTextIcon className="w-4 h-4 text-red-400" />;
    return <FileIcon className="w-4 h-4 text-[#94A3B8]" />;
  };

  const isImage = (type) => ['jpg', 'jpeg', 'png', 'webp'].includes(type);

  return (
    <CollapsibleSection
      icon={FolderOpen}
      title="File Manager"
      description={`${files.length} files • ${formatSize(files.reduce((s, f) => s + (f.size || 0), 0))} total`}
      testid="file-manager-section"
      headerRight={
        <>
          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUpload} className="hidden" data-testid="file-manager-upload-input" />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-[#1E293B] text-[#94A3B8]" data-testid="file-manager-upload-btn">
            <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </>
      }
    >
      <div className="max-h-[400px] overflow-y-auto">
        {loadingFiles ? (
          <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3B82F6] mx-auto"></div></div>
        ) : files.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="bg-[#0A0A0A]/80 border-b border-[#1E293B]">
                <th className="py-2 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Preview</th>
                <th className="py-2 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Filename</th>
                <th className="py-2 px-4 text-right text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Size</th>
                <th className="py-2 px-4 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Modified</th>
                <th className="py-2 px-4 text-right text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E293B]">
              {files.map(f => (
                <tr key={f.name} className="hover:bg-[#1A1A1A]" data-testid={`file-row-${f.name}`}>
                  <td className="py-2 px-4">
                    {isImage(f.type) ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} alt="" className="w-10 h-10 object-cover rounded border border-[#1E293B]" />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A] rounded border border-[#1E293B]">
                        {getFileIcon(f.type)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-2">
                      {getFileIcon(f.type)}
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#F8FAFC] hover:text-[#3B82F6] truncate max-w-[200px]">{f.name}</a>
                    </div>
                  </td>
                  <td className="py-2 px-4 text-right text-sm text-[#94A3B8]">{formatSize(f.size)}</td>
                  <td className="py-2 px-4 text-sm text-[#94A3B8]">{new Date(f.modified).toLocaleDateString()}</td>
                  <td className="py-2 px-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(f.name)} disabled={deleting === f.name} className="text-[#94A3B8] hover:text-red-400 h-8 w-8 p-0" data-testid={`delete-file-${f.name}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-[#94A3B8]">
            <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No files uploaded yet</p>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default SettingsPage;
